import type { JSX } from 'react';
import { useState } from 'react';
import { Logo } from './Logo';
import { signIn, signInWithPasskey, SignInError } from '../data/session';
import { passkeysSupported } from '../data/passkeys';
import { IDP_IS_A_STAND_IN, SEED_LOGINS } from '../config';

/**
 * The address of the last successful sign-in on this browser, so a clinician
 * returning to their own machine types only the password. Kept apart from the
 * tokens, so signing out does not forget it. Only ever written after a sign-in
 * succeeds: a mistyped address is not worth remembering.
 */
const LAST_EMAIL_KEY = 'fatigue.lastEmail';

function rememberEmail(email: string): void {
  window.localStorage.setItem(LAST_EMAIL_KEY, email.trim());
}

/**
 * The whole app until somebody signs in.
 *
 * There is no self-registration on the clinician pool: a login exists because
 * an owner invited it, or because its owner registered the organisation at
 * `/register`, which this links to. `AccountRecovery.EMAIL_ONLY` on the pool
 * means a forgotten password is Cognito's own flow rather than this form's.
 *
 * One error message for every failure, which is not vagueness for its own
 * sake: both pool clients set `prevent_user_existence_errors`, so the server
 * will not say whether an address is registered, and a form that appeared to
 * know better would be inventing it.
 */
export function SignIn(): JSX.Element {
  const [email, setEmail] = useState(() => window.localStorage.getItem(LAST_EMAIL_KEY) ?? '');
  // Decided once, so focus lands where the typing starts and does not move
  // while somebody edits the address.
  const [remembered] = useState(() => email !== '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function withPasskey(): Promise<void> {
    if (email.trim() === '') {
      setError('Enter your email address first, then choose passkey.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await signInWithPasskey(email);
      rememberEmail(email);
      window.location.reload();
    } catch (err) {
      setError(err instanceof SignInError ? err.message : 'Could not sign in with a passkey.');
      setBusy(false);
    }
  }

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(email, password);
      rememberEmail(email);
      // Reload rather than lift the session into state. Everything in the app
      // is scoped to the signed-in person and reads it on mount, so arriving
      // through a fresh load is both simpler and the same thing that happens
      // when a signed-in clinician opens the dashboard tomorrow.
      window.location.reload();
    } catch (err) {
      setError(
        err instanceof SignInError
          ? err.message
          : 'Could not reach the sign-in service. Check your connection.',
      );
      setBusy(false);
    }
  }

  return (
    <main className="signin">
      <form className="signin__card" onSubmit={submit}>
        <h1 className="signin__brand">
          <Logo />
          The Fatigue App
        </h1>
        <p className="signin__sub">Clinician sign-in</p>

        {error !== null && (
          /* Announced, because a sighted user sees it appear and a screen
             reader user otherwise gets silence after pressing the button. */
          <p className="invite__error" role="alert">
            {error}
          </p>
        )}

        <label className="invite__label" htmlFor="signin-email">
          Email address
        </label>
        <input
          id="signin-email"
          className="invite__input"
          type="email"
          autoComplete="username"
          required
          autoFocus={!remembered}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />

        <label className="invite__label signin__spaced" htmlFor="signin-password">
          Password
        </label>
        <input
          id="signin-password"
          className="invite__input"
          type="password"
          autoComplete="current-password"
          required
          autoFocus={remembered}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />

        <button type="submit" className="primary signin__submit" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>

        {/* Only against Cognito: passkeys belong to the deployed site's
            domain, and the local IdP has none. */}
        {!IDP_IS_A_STAND_IN && passkeysSupported() && (
          <button
            type="button"
            className="ghost ghost--wide signin__passkey"
            disabled={busy}
            onClick={() => void withPasskey()}
          >
            Sign in with a passkey
          </button>
        )}

        <p className="signin__note">
          Accounts are created by invitation. If you have not been invited to an organisation,
          ask whoever runs it — or <a href="/register">register a new organisation</a>.
        </p>

        {IDP_IS_A_STAND_IN && <SeedLogins onPick={setEmail} />}
      </form>
    </main>
  );
}

/**
 * Development only, and conspicuous about it.
 *
 * The behaviour worth checking in this product is relational — an owner sees
 * administration and an empty caseload, one clinician runs two cohorts and
 * another runs one, and neither can see the other organisation. Checking that
 * by hand means being able to become each of them, which used to be a dropdown
 * that changed a header. Now it is sign-out and sign-in, because that is what
 * it will be in a real build; this list only saves the typing.
 */
function SeedLogins({ onPick }: { readonly onPick: (email: string) => void }): JSX.Element {
  return (
    <div className="signin__dev">
      <p className="signin__devhead">
        <span className="devswitch__tag">DEV</span>
        Local identity provider — no real authentication happens here.
      </p>
      <ul className="signin__devlist">
        {SEED_LOGINS.map((login) => (
          <li key={login.email}>
            <button type="button" className="ghost" onClick={() => onPick(login.email)}>
              {login.email}
            </button>
            <span>{login.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
